import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { Moon, Sun, User, Lock, Globe, Plus } from 'lucide-react';
import { Type } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './Settings.css';

function Settings() {
  const { user, updatePreferences, selectedBlog: currentBlog, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const [defaultFontSize, setDefaultFontSize] = useState('14');
  const [fontSizeError, setFontSizeError] = useState('');

  // Blog settings - simplified schema: only blog_id (WordPress ID) and domain
  const [blogSettings, setBlogSettings] = useState({
    blog_id: null,
    domain: ''
  });
  const [savingBlogSettings, setSavingBlogSettings] = useState(false);

  // New blog creation
  const [newBlogForm, setNewBlogForm] = useState({
    blog_id: '',
    domain: ''
  });
  const [creatingBlog, setCreatingBlog] = useState(false);

  const handleCreateBlog = async () => {
    if (!newBlogForm.blog_id) {
      toast.error('WordPress Blog ID is required');
      return;
    }
    if (!newBlogForm.domain) {
      toast.error('Domain is required');
      return;
    }

    setCreatingBlog(true);
    try {
      await api.post('/blogs', {
        blog_id: parseInt(newBlogForm.blog_id),
        domain: newBlogForm.domain
      });
      toast.success('Blog created successfully');
      setNewBlogForm({ blog_id: '', domain: '' });
      // Refresh user data to get new blog list
      if (refreshUser) {
        await refreshUser();
      }
      // Reload the page to update the blog context
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create blog');
    } finally {
      setCreatingBlog(false);
    }
  };

  // Load blog settings
  useEffect(() => {
    const loadBlogSettings = async () => {
      if (currentBlog?.id) {
        try {
          const response = await api.get(`/blogs/${currentBlog.id}`);
          setBlogSettings({
            blog_id: response.data.blog_id || null,
            domain: response.data.domain || ''
          });
        } catch (error) {
          console.error('Failed to load blog settings:', error);
        }
      }
    };
    loadBlogSettings();
  }, [currentBlog?.id]);

  const handleSaveBlogSettings = async () => {
    if (!currentBlog?.id) return;

    if (!blogSettings.blog_id) {
      toast.error('WordPress Blog ID is required');
      return;
    }
    if (!blogSettings.domain) {
      toast.error('Domain is required');
      return;
    }

    setSavingBlogSettings(true);
    try {
      await api.put(`/blogs/${currentBlog.id}`, {
        blog_id: blogSettings.blog_id,
        domain: blogSettings.domain
      });
      toast.success('Blog settings saved');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save blog settings');
    } finally {
      setSavingBlogSettings(false);
    }
  };

  const handleFontSizeChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    if (value !== '' && !/^\d+$/.test(value)) {
      setFontSizeError('Please enter numbers only');
      return;
    }
    setDefaultFontSize(value);

    if (value === '') {
      setFontSizeError('Font size is required');
    } else {
      const num = parseInt(value, 10);
      if (num < 8 || num > 72) {
        setFontSizeError('Font size must be between 8 and 72');
      } else {
        setFontSizeError('');
      }
    }
  };

  const handleThemeToggle = async () => {
    toggleTheme();
    try {
      await updatePreferences({ theme: theme === 'light' ? 'dark' : 'light' });
    } catch (error) {
      console.error('Failed to save theme preference');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);

    try {
      await api.put('/auth/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="settings-page">
      <h1>Settings</h1>

      <div className="settings-section">
        <h2>
          <User size={20} />
          Profile
        </h2>
        <div className="profile-info">
          <div className="info-row">
            <label>Name</label>
            <span>{user?.name}</span>
          </div>
          <div className="info-row">
            <label>Email</label>
            <span>{user?.email}</span>
          </div>
          <div className="info-row">
            <label>Role</label>
            <span className="role-badge">
              {user?.role === 'super_admin' ? 'Super Admin' : 'User'}
            </span>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2>Appearance</h2>
        <div className="setting-row">
          <div className="setting-info">
            <h3>Theme</h3>
            <p>Switch between light and dark mode</p>
          </div>
          <button
            className={`theme-toggle ${theme}`}
            onClick={handleThemeToggle}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            <Sun size={18} className="icon-sun" />
            <Moon size={18} className="icon-moon" />
            <span className="toggle-thumb" />
          </button>
        </div>
        <div className="setting-row">
          <div className="setting-info">
            <h3>Default Font Size</h3>
            <p>Set the default font size for new text elements (8-72px)</p>
          </div>
          <div className="font-size-input-wrapper">
            <input
              type="text"
              inputMode="numeric"
              value={defaultFontSize}
              onChange={handleFontSizeChange}
              className={`font-size-input ${fontSizeError ? 'input-error' : ''}`}
              aria-label="Default font size"
            />
            <span className="font-size-unit">px</span>
            {fontSizeError && <span className="error-message">{fontSizeError}</span>}
          </div>
        </div>
      </div>

      {user?.role === 'super_admin' && (
        <div className="settings-section">
          <h2>
            <Type size={20} />
            Font Manager
          </h2>
          <div className="setting-row">
            <div className="setting-info">
              <h3>Global Word Fonts</h3>
              <p>Upload, replace, roll back, and activate fonts for all Word documents.</p>
            </div>
            <button type="button" className="btn btn-primary settings-font-manager-button" onClick={() => navigate('/settings/fonts')}>
              Manage Fonts
            </button>
          </div>
        </div>
      )}

      {/* Create Blog - for super admin when no blogs exist */}
      {user?.role === 'super_admin' && (!user?.blogs || user.blogs.length === 0) && (
        <div className="settings-section">
          <h2>
            <Plus size={20} />
            Create Blog
          </h2>
          <p className="section-description">
            No blogs configured. Create a blog to get started with document building.
          </p>
          <div className="setting-row">
            <div className="setting-info">
              <h3>WordPress Blog ID</h3>
              <p>Numeric identifier for WordPress multisite (e.g., 860, 271)</p>
            </div>
            <input
              type="number"
              value={newBlogForm.blog_id}
              onChange={(e) => setNewBlogForm(prev => ({ ...prev, blog_id: e.target.value }))}
              className="text-input"
              placeholder="e.g., 860"
              aria-label="WordPress Blog ID"
            />
          </div>
          <div className="setting-row">
            <div className="setting-info">
              <h3>Domain</h3>
              <p>WordPress site domain (also used as display name)</p>
            </div>
            <input
              type="text"
              value={newBlogForm.domain}
              onChange={(e) => setNewBlogForm(prev => ({ ...prev, domain: e.target.value }))}
              className="text-input"
              placeholder="e.g., example.com"
              aria-label="Domain"
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCreateBlog}
            disabled={creatingBlog}
          >
            {creatingBlog ? 'Creating...' : 'Create Blog'}
          </button>
        </div>
      )}

      {/* Blog Settings - only for super admin when blog exists */}
      {user?.role === 'super_admin' && currentBlog && (
        <div className="settings-section">
          <h2>
            <Globe size={20} />
            Blog Settings ({currentBlog.name || currentBlog.domain})
          </h2>
          <p className="section-description">
            Configure WordPress integration settings for this blog.
          </p>
          <div className="setting-row">
            <div className="setting-info">
              <h3>WordPress Blog ID</h3>
              <p>Numeric identifier for WordPress multisite (e.g., 860, 271)</p>
            </div>
            <input
              type="number"
              value={blogSettings.blog_id || ''}
              onChange={(e) => setBlogSettings(prev => ({ ...prev, blog_id: e.target.value ? parseInt(e.target.value) : null }))}
              className="text-input"
              placeholder="e.g., 860"
              aria-label="WordPress Blog ID"
            />
          </div>
          <div className="setting-row">
            <div className="setting-info">
              <h3>Domain</h3>
              <p>WordPress site domain for API communication (also used as display name)</p>
            </div>
            <input
              type="text"
              value={blogSettings.domain || ''}
              onChange={(e) => setBlogSettings(prev => ({ ...prev, domain: e.target.value }))}
              className="text-input"
              placeholder="e.g., example.com"
              aria-label="Domain"
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleSaveBlogSettings}
            disabled={savingBlogSettings}
          >
            {savingBlogSettings ? 'Saving...' : 'Save Blog Settings'}
          </button>
        </div>
      )}

      {user?.auth_source !== 'wordpress' && (
        <div className="settings-section">
          <h2>
            <Lock size={20} />
            Change Password
          </h2>
          <form onSubmit={handlePasswordChange} className="password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                required
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                required
                minLength={8}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={changingPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default Settings;
